import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Loader2, Sparkles, GripVertical } from 'lucide-react';
import { Badge } from '../ui/badge';

const DEFAULT_CONFIG = {
  mode: 'combo',
  include_global_library: true,
  include_user_library: true,
  superset_id: null,
};

export default function BlockSessionSourcePanel({ scope, scopeId, inheritedLabel, initialConfig, onChanged }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG, ...(initialConfig || {}) });
  const [supersets, setSupersets] = useState([]);
  const [activeSuperset, setActiveSuperset] = useState(null);

  const load = useCallback(async () => {
    if (!scopeId) return;
    setLoading(true);
    try {
      const [ssRes] = await Promise.all([
        api.get('/api/training/periodization/supersets', { params: { scope, scope_id: scopeId } }),
      ]);
      setSupersets(ssRes.data.supersets || []);
      const linked = (ssRes.data.supersets || []).find((s) => s.id === config.superset_id);
      setActiveSuperset(linked || ssRes.data.supersets?.[0] || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => {
    if (initialConfig) setConfig({ ...DEFAULT_CONFIG, ...initialConfig });
  }, [initialConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const saveConfig = async (next) => {
    setSaving(true);
    try {
      if (scope === 'block') {
        await api.put(`/api/training/periodization/blocks/${scopeId}`, {
          session_source_config: next,
        });
      } else if (scope === 'phase') {
        await api.put(`/api/training/periodization/phases/${scopeId}`, {
          session_source_config: next,
        });
      }
      setConfig(next);
      onChanged?.();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const suggestSuperset = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/training/periodization/supersets/suggest', {
        query: 'running workout quality',
        limit: 6,
      });
      let superset = supersets[0];
      if (!superset) {
        const created = await api.post('/api/training/periodization/supersets', {
          scope,
          scope_id: scopeId,
          name: t('periodization.superset.defaultName'),
        });
        superset = created.data.superset;
      }
      for (const item of res.data.items || []) {
        await api.post(`/api/training/periodization/supersets/${superset.id}/items`, item);
      }
      const next = { ...config, superset_id: superset.id };
      await saveConfig(next);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!scopeId) return null;

  return (
    <div className='rounded-lg border border-border p-3 space-y-3'>
      <div className='flex items-center justify-between gap-2'>
        <p className='text-sm font-medium'>{t('periodization.sessionSource.title')}</p>
        {inheritedLabel && (
          <Badge variant='outline' className='text-[10px]'>{inheritedLabel}</Badge>
        )}
      </div>
      {loading ? (
        <Loader2 className='h-5 w-5 animate-spin text-primary' />
      ) : (
        <>
          <div className='space-y-1.5'>
            <Label className='text-xs'>{t('periodization.sessionSource.mode')}</Label>
            <Select
              value={config.mode}
              onValueChange={(v) => saveConfig({ ...config, mode: v })}
            >
              <SelectTrigger className='h-8 text-xs'><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value='combo'>{t('periodization.sessionSource.combo')}</SelectItem>
                <SelectItem value='superset_only'>{t('periodization.sessionSource.supersetOnly')}</SelectItem>
                <SelectItem value='library_only'>{t('periodization.sessionSource.libraryOnly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <Label className='text-xs'>{t('periodization.sessionSource.globalLibrary')}</Label>
            <Switch
              checked={!!config.include_global_library}
              onCheckedChange={(v) => saveConfig({ ...config, include_global_library: v })}
            />
          </div>
          <div className='flex items-center justify-between gap-2'>
            <Label className='text-xs'>{t('periodization.sessionSource.userLibrary')}</Label>
            <Switch
              checked={!!config.include_user_library}
              onCheckedChange={(v) => saveConfig({ ...config, include_user_library: v })}
            />
          </div>
          <div className='flex flex-wrap gap-2'>
            <Button type='button' size='sm' variant='outline' className='gap-1 text-xs' onClick={suggestSuperset} disabled={saving}>
              {saving ? <Loader2 className='h-3 w-3 animate-spin' /> : <Sparkles className='h-3 w-3' />}
              {t('periodization.superset.suggest')}
            </Button>
          </div>
          {activeSuperset?.items?.length > 0 && (
            <ul className='space-y-1 max-h-32 overflow-y-auto'>
              {activeSuperset.items.map((item) => (
                <li key={item.id} className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <GripVertical className='h-3 w-3 shrink-0' />
                  <span className='truncate flex-1'>{item.title}</span>
                  <span>P{item.priority}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
